import { formatMultiCurrency, getPreferredCurrency } from '../utils/multiCurrency';

/**
 * DualCurrency - Display amounts in both USD and DOP
 * 
 * @param {number} usd - Amount in USD (optional if dop provided)
 * @param {number} dop - Amount in DOP (optional if usd provided)
 * @param {string} role - User role for currency preference ('sender', 'merchant')
 * @param {string} size - Size variant ('sm', 'md', 'lg')
 * @param {boolean} showSecondary - Whether to show secondary currency
 * @param {string} primaryColor - Tailwind color class for primary amount
 */
export function DualCurrency({ 
  usd = null, 
  dop = null, 
  role = 'sender',
  size = 'md',
  showSecondary = true,
  primaryColor = 'text-slate-900'
}) {
  const primary = getPreferredCurrency(role);
  const formatted = formatMultiCurrency(usd, dop, primary);
  
  const sizeClasses = {
    sm: { primary: 'text-sm font-medium', secondary: 'text-xs' },
    md: { primary: 'text-base font-semibold', secondary: 'text-sm' },
    lg: { primary: 'text-2xl font-bold', secondary: 'text-sm' },
    xl: { primary: 'text-3xl font-bold', secondary: 'text-base' }
  };
  
  const classes = sizeClasses[size] || sizeClasses.md;
  
  return (
    <span className="inline-flex flex-col">
      <span className={`${classes.primary} ${primaryColor}`}>
        {formatted.primary}
      </span>
      {showSecondary && (
        <span className={`${classes.secondary} text-slate-500`}>
          ≈{formatted.secondary}
        </span>
      )}
    </span>
  );
}

/**
 * DualCurrencyInline - Display amounts inline (e.g., "$50 (≈RD$2,925)")
 */
export function DualCurrencyInline({ 
  usd = null, 
  dop = null, 
  role = 'sender',
  className = ''
}) {
  const primary = getPreferredCurrency(role);
  const formatted = formatMultiCurrency(usd, dop, primary);
  
  return (
    <span className={className}>
      {formatted.combined}
    </span>
  );
}

/**
 * CurrencyBadge - Compact currency display with badge styling
 */
export function CurrencyBadge({
  usd = null,
  dop = null,
  role = 'sender',
  variant = 'default'
}) {
  const primary = getPreferredCurrency(role);
  const formatted = formatMultiCurrency(usd, dop, primary);
  
  const variants = {
    default: 'bg-slate-100 text-slate-700',
    success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700',
    info: 'bg-blue-100 text-blue-700'
  };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${variants[variant]}`}>
      <span>{formatted.primary}</span>
      <span className="text-xs opacity-75">({formatted.secondary})</span>
    </span>
  );
}

export default DualCurrency;
